import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useApi } from '../api-context';
import { queryKeys } from '../query-keys';
import {
  adminDeliveryListSchema,
  cabinetEmailLimitSchema,
  emailActivityListSchema,
  queueHealthSchema,
  type AdminDeliveryList,
  type CabinetEmailLimit,
  type EmailActivityFilter,
  type EmailActivityList,
  type AdminDeliveryGroup,
  type MessageSender,
  type QueueHealth,
} from '../schemas';
import { useToken } from './use-token';

export type EmailActivityParams = {
  page?: number;
  limit?: number;
  search?: string;
  filter?: EmailActivityFilter;
  /** Restreint à un cabinet : sa ligne, vue depuis sa fiche. */
  cabinetId?: string;
};

/**
 * Le suivi des envois, côté plateforme : un résumé, puis une ligne par
 * cabinet avec son plafond, son volume et son dernier refus.
 *
 * `refetchInterval` : l'écran sert pendant un incident — « est-ce que ça
 * repart ? » — et une page qu'il faut recharger à la main pour le savoir
 * répond mal à cette question. Une minute suffit, les compteurs sont horaires.
 */
export function useAdminEmailActivity(params: EmailActivityParams = {}) {
  const api = useApi();
  const token = useToken();

  const query = new URLSearchParams();
  if (params.page) query.set('page', String(params.page));
  if (params.limit) query.set('limit', String(params.limit));
  if (params.search) query.set('search', params.search);
  if (params.filter && params.filter !== 'all') query.set('filter', params.filter);
  if (params.cabinetId) query.set('cabinetId', params.cabinetId);

  return useQuery({
    queryKey: queryKeys.adminEmails.activity(params as Record<string, unknown>),
    queryFn: () =>
      api.get<EmailActivityList>(`/admin/emails?${query.toString()}`, {
        schema: emailActivityListSchema,
      }),
    enabled: token !== null,
    refetchInterval: 60_000,
  });
}

export type AdminDeliveriesParams = {
  page?: number;
  limit?: number;
  /** Restreint à un cabinet : la même liste, vue depuis la fiche d'un compte. */
  cabinetId?: string;
  status?: AdminDeliveryGroup | '';
  sender?: MessageSender | '';
  /** Catégorie du message (`catalogKey`) : demande d'avis, rappel… */
  type?: string;
  /** Recherche sur le nom du cabinet. */
  search?: string;
};

/**
 * Les envois, paginés — métadonnées seules : ni objet, ni corps, et le domaine
 * du destinataire à la place de son adresse.
 *
 * Un seul hook pour deux écrans : la liste globale de l'onglet « Envois » et
 * celle de la fiche d'un compte, qui n'est que la même filtrée sur son cabinet.
 */
export function useAdminDeliveries(params: AdminDeliveriesParams = {}) {
  const api = useApi();
  const token = useToken();

  const query = new URLSearchParams();
  if (params.page) query.set('page', String(params.page));
  if (params.limit) query.set('limit', String(params.limit));
  if (params.cabinetId) query.set('cabinetId', params.cabinetId);
  if (params.status) query.set('status', params.status);
  if (params.sender) query.set('sender', params.sender);
  if (params.type) query.set('type', params.type);
  if (params.search) query.set('search', params.search);

  return useQuery({
    queryKey: queryKeys.adminEmails.deliveries(params as Record<string, unknown>),
    queryFn: () =>
      api.get<AdminDeliveryList>(`/admin/emails/deliveries?${query.toString()}`, {
        schema: adminDeliveryListSchema,
      }),
    enabled: token !== null,
    // Changing tab or page keeps the previous list on screen (counts, pagination included)
    // until the next one arrives, instead of emptying the whole table for the wait.
    placeholderData: keepPreviousData,
  });
}

/**
 * Règle le plafond horaire d'un cabinet. `null` le rend au plafond de la
 * plateforme.
 */
export function useSetCabinetEmailLimit() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ cabinetId, hourlyLimit }: { cabinetId: string; hourlyLimit: number | null }) =>
      api.patch<CabinetEmailLimit>(
        `/admin/emails/cabinets/${cabinetId}/limit`,
        { hourlyLimit },
        { schema: cabinetEmailLimitSchema },
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.adminEmails.all() }),
  });
}

/**
 * L'état de la file d'attente : worker vivant, envois en retard, cabinets en
 * pause. Relu chaque minute, comme le reste de l'écran : il sert pendant un
 * incident.
 */
export function useAdminQueueHealth() {
  const api = useApi();
  const token = useToken();

  return useQuery({
    queryKey: queryKeys.adminEmails.queue(),
    queryFn: () => api.get<QueueHealth>('/admin/emails/queue', { schema: queueHealthSchema }),
    enabled: token !== null,
    refetchInterval: 60_000,
  });
}
